/* DER paid-search offer.
 *
 * A private percentage for visitors who arrive from an approved Google Ads or
 * Microsoft Ads URL. Not a public sale, not a coupon code, and nothing a
 * customer has to remember or type.
 *
 * What this file does, and nothing else:
 *   1. reads der_offer=<percent> from the landing URL and, if the percentage
 *      is one we allow, records it in a first-party cookie for a short window;
 *   2. copies it onto the cart as the _der_offer attribute, which is the only
 *      thing the DER Supply Pricing function reads;
 *   3. reveals one short line of confirmation on the PDP and in the cart;
 *   4. stands down the moment the window closes or a discount code lands on
 *      the cart, so a doctor's better percentage is the only one in play.
 *
 * It never computes a price. Every number a shopper sees is rendered by Liquid
 * from the live cart after the function has priced it, exactly as the cart
 * upsell and package protection modules work.
 *
 * Kill switch: theme setting "Enable paid-search offer". With it off, nothing
 * here runs, no attribute is ever written, and the pricing function returns
 * precisely what it returned before this existed.
 */

(() => {
  const CFG = window.derPaidOffer || {};
  if (!CFG.enabled) return;

  const COOKIE = 'der_offer';
  const ATTR = '_der_offer';
  const QA_KEY = 'der_paid_offer_qa';
  const QA_PARAMS = ['der_offer_qa', 'der_paid_offer_qa'];

  /* Percentages this store will honour. Mirrors PAID_OFFER_ALLOWED_PERCENTS in
   * the pricing function. A value outside this list is treated as no offer, so
   * a hand-typed der_offer=50 does nothing here and nothing at checkout.
   *
   * The function is the authority. Adding 15 here without adding it to
   * PAID_OFFER_ALLOWED_PERCENTS in the function would show a message the
   * checkout does not honour, which is the one failure mode worth guarding. */
  const ALLOWED = [10];

  const nowMs = () => Date.now();

  function params() {
    try {
      return new URLSearchParams(window.location.search);
    } catch (e) {
      return new URLSearchParams('');
    }
  }

  /* ---------- QA gate, same shape as der_cart_upsell_qa and der_pp_qa ---------- */

  function qaEnabled() {
    if (!CFG.qaOnly) return true;
    const p = params();
    let flag = null;
    for (let i = 0; i < QA_PARAMS.length; i += 1) {
      const v = p.get(QA_PARAMS[i]);
      if (v !== null) { flag = v; break; }
    }
    try {
      if (flag === '1') { localStorage.setItem(QA_KEY, '1'); return true; }
      if (flag === '0') { localStorage.removeItem(QA_KEY); return false; }
      return localStorage.getItem(QA_KEY) === '1';
    } catch (e) {
      /* Storage refused. The parameter alone decides, so QA still works in a
       * private window. */
      return flag === '1';
    }
  }

  /* ---------- first-party cookie ---------- */

  function readCookie() {
    let raw = null;
    try {
      const match = document.cookie.match(new RegExp('(?:^|; )' + COOKIE + '=([^;]*)'));
      raw = match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
    if (!raw) return null;
    let data;
    try { data = JSON.parse(raw); } catch (e) { return null; }
    if (!data || ALLOWED.indexOf(data.p) === -1) return null;
    if (!data.e || data.e <= nowMs()) return null;
    return data;
  }

  function writeCookie(data) {
    try {
      const maxAge = Math.max(0, Math.floor((data.e - nowMs()) / 1000));
      document.cookie = COOKIE + '=' + encodeURIComponent(JSON.stringify(data))
        + '; path=/; max-age=' + maxAge + '; SameSite=Lax'
        + (window.location.protocol === 'https:' ? '; Secure' : '');
    } catch (e) { /* Cookies refused. The offer simply does not persist. */ }
  }

  function clearCookie() {
    try {
      document.cookie = COOKIE + '=; path=/; max-age=0; SameSite=Lax';
    } catch (e) { /* nothing to do */ }
  }

  /* ---------- eligibility ---------- */

  /* der_offer is the trigger, on its own and on purpose. utm_source is not,
   * because UTMs are used in email and elsewhere; the source fields below are
   * recorded for reporting only and never decide anything. */
  function captureFromUrl() {
    const p = params();
    const raw = p.get('der_offer');
    if (raw === null) return null;
    if (!/^[0-9]{1,3}$/.test(raw.trim())) return null;
    const percent = parseInt(raw.trim(), 10);
    if (ALLOWED.indexOf(percent) === -1) return null;

    const hours = Number(CFG.hours) > 0 ? Number(CFG.hours) : 24;
    const src = (p.get('utm_source') || (p.get('msclkid') ? 'bing' : '') || (p.get('gclid') ? 'google' : '') || '')
      .toString().toLowerCase().slice(0, 40);

    return {
      p: percent,
      e: nowMs() + hours * 3600 * 1000,
      s: src,
      m: (p.get('utm_medium') || '').toString().toLowerCase().slice(0, 40),
      c: (p.get('utm_campaign') || '').toString().slice(0, 80),
      l: (window.location.pathname || '').slice(0, 120)
    };
  }

  /* ---------- cart ---------- */

  function cartCodes() {
    const host = document.querySelector('[data-der-po-codes]');
    if (host) {
      return (host.getAttribute('data-der-po-codes') || '')
        .split(',').map((c) => c.trim()).filter(Boolean);
    }
    return Array.isArray(CFG.cartCodes) ? CFG.cartCodes : [];
  }

  /* Section re-render, same shape as der-package-protection.js.
   *
   * Writing the attribute changes every price in the cart, but Liquid has
   * already rendered by the time we write. Without asking for the sections
   * back, the first view of a cart shows list prices, the old total and a
   * free-shipping notice computed on the wrong subtotal: measured live on
   * 2026-09-14, the same cart said "FREE Standard Shipping unlocked" before
   * the write and "You're $2.86 away" after a reload. Overstating free
   * shipping is the one failure mode the cart copy must never have, so the
   * write asks for the sections and swaps them in.
   *
   * No price is computed here. Everything swapped in came from Liquid, priced
   * by the function, which is the same discipline the rest of the cart uses. */
  function sectionsToRequest() {
    const names = ['cart-icon-bubble', 'free-shipping-notice'];
    const drawer = document.getElementById('cart-drawer');
    if (drawer && drawer.closest('.shopify-section')) {
      names.push(drawer.closest('.shopify-section').id.replace('shopify-section-', ''));
    }
    const summary = document.getElementById('cart-summary');
    if (summary && summary.dataset.section) names.push(summary.dataset.section);
    const items = document.getElementById('cart-items');
    if (items && items.dataset.section) names.push(items.dataset.section);
    return Array.from(new Set(names));
  }

  function swap(data, id, sectionName, selector) {
    const host = document.getElementById(id);
    const html = data.sections && data.sections[sectionName];
    if (!host || !html) return;
    const tmpl = document.createElement('template');
    tmpl.innerHTML = html;
    const source = tmpl.content.querySelector(selector);
    const dest = host.matches(selector) ? host : (host.querySelector(selector) || host);
    if (source) dest.innerHTML = source.innerHTML;
  }

  function render(data) {
    if (!data || !data.sections) return;
    const drawer = document.getElementById('cart-drawer');
    const drawerSection = drawer && drawer.closest('.shopify-section')
      ? drawer.closest('.shopify-section').id.replace('shopify-section-', '')
      : null;
    const items = document.getElementById('cart-items');
    const itemsSection = items ? items.dataset.section : null;
    const summary = document.getElementById('cart-summary');

    if (itemsSection) swap(data, 'cart-items', itemsSection, 'cart-items');
    if (drawerSection) {
      swap(data, 'cart-items', drawerSection, 'cart-items');
      swap(data, 'cart-drawer', drawerSection, '.cart-drawer__summary');
    }
    if (summary && summary.dataset.section) {
      swap(data, 'cart-summary', summary.dataset.section, '.cart__summary');
    }
    swap(data, 'free-shipping-notice', 'free-shipping-notice', '.free-shipping-notice');

    /* The swap replaces the summary's markup, and fresh markup always arrives
     * with the message hidden. Announce the change the way the rest of this
     * theme does; der-package-protection.js ends its render the same way, and
     * every cart module here listens for it.
     *
     * The MutationObserver in watch() is deliberately NOT relied on for this.
     * Measured live on 2026-09-14: a mutation inside #cart-summary did not
     * repaint, while this event did. The observer stays as a second line of
     * defence for markup the theme replaces on its own. */
    document.dispatchEvent(new CustomEvent('on:cart:updated', { bubbles: true }));
    repaintSoon();
  }

  let writing = false;

  function updateCart(attributes) {
    if (writing) return Promise.resolve();
    writing = true;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes,
        sections: sectionsToRequest(),
        sections_url: window.location.pathname
      })
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data) render(data); })
      .catch(() => {
        /* A failed attribute write must never block a purchase. The shopper
         * pays the normal price, which is the safe direction to fail in. */
      })
      .finally(() => { writing = false; });
  }

  function attrsOn(data) {
    return {
      [ATTR]: String(data.p),
      _der_offer_src: data.s || '',
      _der_offer_medium: data.m || '',
      _der_offer_campaign: data.c || '',
      _der_offer_landing: data.l || '',
      _der_offer_exp: String(data.e)
    };
  }

  const ATTRS_OFF = {
    [ATTR]: '',
    _der_offer_src: '',
    _der_offer_medium: '',
    _der_offer_campaign: '',
    _der_offer_landing: '',
    _der_offer_exp: ''
  };

  /* ---------- messaging ---------- */

  /* Every write here MUST be idempotent.
   *
   * The cart-page message sits inside #cart-summary, which the observer in
   * watch() watches with childList and subtree. Writing textContent is a
   * childList mutation, so an unconditional write re-entered the observer,
   * which repainted, which wrote again: an infinite loop that froze the cart
   * page for exactly the visitors this feature is for. Comparing before
   * writing breaks it at the source, and is cheaper than disconnecting and
   * reconnecting the observer around every paint. Do not "simplify" this
   * back to an unconditional assignment. */
  function paint(active, percent) {
    document.querySelectorAll('[data-der-po]').forEach((el) => {
      if (active && percent) {
        const tpl = el.getAttribute('data-der-po-template');
        const text = el.querySelector('[data-der-po-text]');
        /* The percentage actually in force on this visit, never the theme
         * setting, so the line cannot promise a number checkout will not
         * honour. */
        if (tpl && text) {
          const next = tpl.split('[percent]').join(String(percent));
          if (text.textContent !== next) text.textContent = next;
        }
      }
      if (el.hidden !== !active) el.hidden = !active;
    });
  }

  /* ---------- the one decision this module makes ---------- */

  function sync() {
    /* Read first, and before any early return, so ?der_offer_qa=1 sticks even
     * on a page that carries no offer parameter. The opt-in link has to work
     * on its own or QA on the live theme is guesswork. */
    const qa = qaEnabled();

    const captured = captureFromUrl();
    if (captured) writeCookie(captured);

    const data = captured || readCookie();
    const codes = cartCodes();
    const cartHasOffer = String(CFG.cartOffer || '') !== '';

    /* Doctor and referral codes win, always. Shopify would already let the
     * code replace this discount, but clearing the attribute makes the
     * outcome deterministic and keeps the reporting honest: an order that
     * used a doctor code is not also tagged as a paid-search offer order. */
    if (codes.length > 0) {
      paint(false);
      if (cartHasOffer) {
        CFG.cartOffer = '';
        updateCart(ATTRS_OFF);
      }
      return;
    }

    if (!data) {
      paint(false);
      if (cartHasOffer) {
        CFG.cartOffer = '';
        updateCart(ATTRS_OFF);
      }
      clearCookie();
      return;
    }

    paint(qa, data.p);

    /* Only written when the cart has something in it and does not already
     * carry the right value, so the steady state costs no requests at all. */
    if (Number(CFG.cartCount) > 0 && String(CFG.cartOffer || '') !== String(data.p)) {
      /* Set before the request, not after. render() dispatches nothing itself,
       * but the section swap mutates observed subtrees, and a sync racing in
       * on that must not queue a second identical write. */
      CFG.cartOffer = String(data.p);
      updateCart(attrsOn(data));
    }
  }

  /* Repaint, without depending on being told.
   *
   * Fresh cart markup always arrives with the message hidden, so something has
   * to turn it back on after every re-render. Three things try, because on
   * 2026-09-14 each of the first two was observed to miss on its own:
   *
   *   1. the theme's cart events. These fire reliably, but the theme's own
   *      add-to-cart dispatches before it finishes swapping the drawer, so a
   *      repaint on the event alone ran against markup that was replaced a
   *      moment later and the drawer line stayed hidden.
   *   2. a MutationObserver. Observers attached to #cart-drawer and
   *      #cart-summary did not fire for the theme's swaps, while a freshly
   *      attached observer on the same node did. Rather than keep guessing at
   *      why, this now watches the document root, which cannot be replaced out
   *      from under it.
   *   3. a short trailing schedule after each of the above, which catches a
   *      swap that lands just after a repaint.
   *
   * All three converge on repaint(), every write in paint() is idempotent, and
   * repaints are coalesced to one per tick, so running three times costs
   * nothing and the belt-and-braces is genuinely free. */
  let repaintQueued = false;

  function repaint() {
    if (repaintQueued) return;
    repaintQueued = true;
    setTimeout(() => {
      repaintQueued = false;
      const live = readCookie();
      const ok = live !== null && cartCodes().length === 0 && qaEnabled();
      paint(ok, ok ? live.p : null);
    }, 0);
  }

  /* A swap can land just after a repaint. These are cheap: each one is a
   * querySelectorAll over a handful of nodes and, when nothing changed, zero
   * DOM writes. */
  function repaintSoon() {
    repaint();
    [150, 400, 900].forEach((ms) => setTimeout(repaint, ms));
  }

  function watch() {
    ['on:cart:add', 'on:cart:updated', 'on:line-item:change', 'on:cart:error'].forEach((evt) => {
      document.addEventListener(evt, () => {
        /* A first add can turn an empty cart into one that needs the
         * attribute, so re-read the count from the live cart markup. */
        const host = document.querySelector('[data-der-po-count]');
        if (host) CFG.cartCount = Number(host.getAttribute('data-der-po-count')) || 1;
        else CFG.cartCount = Math.max(1, Number(CFG.cartCount) || 1);
        sync();
        repaintSoon();
      });
    });

    if (typeof MutationObserver !== 'undefined' && document.documentElement) {
      new MutationObserver(repaint)
        .observe(document.documentElement, { childList: true, subtree: true });
    }

    /* Back button out of the bfcache restores the old DOM with the old
     * decision baked in. Re-decide. */
    window.addEventListener('pageshow', (evt) => { if (evt.persisted) { sync(); repaintSoon(); } });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { sync(); watch(); });
  } else {
    sync();
    watch();
  }
})();
